/*
 * The navbar's behavior (sections/bakes-header.liquid), a port of the site's
 * components/Navbar.tsx and components/NavDropdown.tsx. Plain JS, no build
 * step. The markup is server-rendered; this adds:
 *
 * - The solid bar: transparent over the page, solid (and blurred) once the
 *   page has scrolled past 20px or while a menu or the sheet is open, with a
 *   hairline only when scrolled.
 * - The MUSIC and STORE dropdowns (1024px and up). The trigger stays a plain
 *   link; only the caret toggles. Where hover is real (a mouse on a device
 *   whose hover is real), resting on the trigger or the caret for
 *   OPEN_DELAY_MS opens the panel, and leaving closes it after CLOSE_GRACE_MS,
 *   time enough to cross into a panel wider than its trigger. Escape (focus
 *   goes back to the caret), a press outside, a followed link and focus moving
 *   elsewhere close it. Up and Down from the caret open it on the first or
 *   last link; in the panel they step and wrap, Home and End jump. One panel
 *   is open at a time.
 * - The sheet below 1024px: Escape, the dimmed area and a followed link close
 *   it, the page under it cannot scroll (locked on <html>, since the body is
 *   overflow-x hidden), and widening the window past 1024px closes it. While
 *   it is open, Tab and Shift+Tab cycle through the menu button and the
 *   sheet's links only, and Escape hands focus back to the menu button
 *   (2026-10-26 review). Another dialog on top (the cart drawer) keeps its
 *   own keys.
 * - The cart buttons: a click opens the cart drawer (bakes:cart-open); the
 *   count follows bakes:cart-updated, whose detail is the Ajax API cart.
 * - The ?look=bakes lead on the STORE thumbnails (the site's lib/look).
 * - Motion preview (docs/motion-preview.md): the header count's flash
 *   (mo-cart), the home hero logo bar's one draw and the section rules'
 *   draw on entry (mo-lines), and the touch listener iOS needs for the
 *   press states (mo-hover).
 */
(function () {
  "use strict";

  var NAV_SCROLL_PX = 20;
  var FULL_ROW_PX = 1024;
  var OPEN_DELAY_MS = 120;
  var CLOSE_GRACE_MS = 250;
  var HOVER_CLICK_MS = 500;
  var HOVER_QUERY = "(hover: hover) and (pointer: fine)";
  /* Everything Tab can land on inside the sheet, for its focus trap. */
  var SHEET_FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  var nav = null;
  var scrolled = false;
  var sheetOpen = false;
  var openMenu = null;
  var dropdowns = [];
  var sheetCleanup = null;
  var docListenersBound = false;

  function setSolid() {
    if (!nav) return;
    var solid = scrolled || sheetOpen || openMenu !== null;
    nav.style.backgroundColor = solid ? "rgba(8,11,16,0.97)" : "transparent";
    nav.style.backdropFilter = solid ? "blur(12px)" : "none";
    nav.style.webkitBackdropFilter = solid ? "blur(12px)" : "none";
    nav.style.boxShadow = scrolled ? "0 1px 0 var(--line)" : "none";
  }

  function onScroll() {
    var now = window.scrollY > NAV_SCROLL_PX;
    if (now === scrolled) return;
    scrolled = now;
    setSolid();
  }

  /* ---------- Dropdowns ---------- */

  function panelLinks(dd) {
    return Array.prototype.slice.call(dd.panel.querySelectorAll("a"));
  }

  function focusLink(dd, which) {
    var links = panelLinks(dd);
    var link = links[which === "first" ? 0 : links.length - 1];
    if (link) link.focus();
  }

  function hoverable(e) {
    return e.pointerType === "mouse" && window.matchMedia(HOVER_QUERY).matches;
  }

  function render(dd, open) {
    dd.caret.setAttribute("aria-expanded", open ? "true" : "false");
    dd.caret.setAttribute("aria-label", (open ? "Close " : "Open ") + dd.label + " menu");
    dd.panel.hidden = !open;
  }

  function openDropdown(dd) {
    if (openMenu === dd) return;
    if (openMenu) closeDropdown(openMenu);
    openMenu = dd;
    render(dd, true);
    setSolid();

    var which = dd.focusOnOpen;
    dd.focusOnOpen = null;
    if (which) focusLink(dd, which);

    var onKey = function (e) {
      if (e.key !== "Escape") return;
      var inside = dd.root.contains(document.activeElement);
      closeDropdown(dd);
      if (inside) dd.caret.focus();
    };
    var onDown = function (e) {
      if (!dd.root.contains(e.target)) closeDropdown(dd);
    };
    var onHistory = function () { closeDropdown(dd); };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onDown);
    window.addEventListener("popstate", onHistory);
    dd.cleanup = function () {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onDown);
      window.removeEventListener("popstate", onHistory);
      window.clearTimeout(dd.timers.close);
    };
  }

  function closeDropdown(dd) {
    if (openMenu !== dd) return;
    openMenu = null;
    render(dd, false);
    if (dd.cleanup) {
      dd.cleanup();
      dd.cleanup = null;
    }
    setSolid();
  }

  function setupDropdown(root) {
    var dd = {
      root: root,
      head: root.querySelector(".nav-dd__head"),
      caret: root.querySelector("[data-nav-caret]"),
      panel: root.querySelector("[data-nav-panel]"),
      label: root.getAttribute("data-label") || "",
      timers: { open: 0, close: 0, hoverOpenedAt: 0 },
      focusOnOpen: null,
      cleanup: null
    };
    if (!dd.head || !dd.caret || !dd.panel) return null;
    var t = dd.timers;

    dd.head.addEventListener("pointerenter", function (e) {
      if (!hoverable(e)) return;
      window.clearTimeout(t.close);
      if (openMenu === dd) return;
      window.clearTimeout(t.open);
      t.open = window.setTimeout(function () {
        t.hoverOpenedAt = Date.now();
        openDropdown(dd);
      }, OPEN_DELAY_MS);
    });
    dd.head.addEventListener("pointerleave", function () {
      window.clearTimeout(t.open);
    });

    root.addEventListener("pointerenter", function () {
      window.clearTimeout(t.close);
    });
    root.addEventListener("pointerleave", function (e) {
      window.clearTimeout(t.open);
      window.clearTimeout(t.close);
      if (openMenu === dd && hoverable(e)) {
        t.close = window.setTimeout(function () { closeDropdown(dd); }, CLOSE_GRACE_MS);
      }
    });

    dd.caret.addEventListener("click", function (e) {
      window.clearTimeout(t.open);
      if (openMenu === dd) {
        // A pointer click right after hover opened the panel was meant to
        // open it: leave it up. Enter or Space (a click with no click count)
        // always toggles.
        if (e.detail === 0 || Date.now() - t.hoverOpenedAt > HOVER_CLICK_MS) closeDropdown(dd);
        return;
      }
      t.hoverOpenedAt = 0;
      if (e.detail === 0) dd.focusOnOpen = "first";
      openDropdown(dd);
    });

    dd.caret.addEventListener("keydown", function (e) {
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
      e.preventDefault();
      var which = e.key === "ArrowDown" ? "first" : "last";
      if (openMenu === dd) {
        focusLink(dd, which);
        return;
      }
      window.clearTimeout(t.open);
      t.hoverOpenedAt = 0;
      dd.focusOnOpen = which;
      openDropdown(dd);
    });

    // Up and Down step through the links and wrap; Home and End jump. Tab
    // keeps its usual order.
    dd.panel.addEventListener("keydown", function (e) {
      var links = panelLinks(dd);
      var last = links.length - 1;
      var at = links.indexOf(document.activeElement);
      var to =
        e.key === "ArrowDown" ? (at < 0 || at === last ? 0 : at + 1)
        : e.key === "ArrowUp" ? (at <= 0 ? last : at - 1)
        : e.key === "Home" ? 0
        : e.key === "End" ? last
        : -1;
      if (to < 0) return;
      e.preventDefault();
      links[to].focus();
    });

    // Focus that moves to an element outside closes it. A blur to nothing
    // does not: Safari does not focus a link on click, so a press on a panel
    // link blurs to nothing, and closing then would pull the link out from
    // under the click.
    root.addEventListener("focusout", function (e) {
      var to = e.relatedTarget;
      if (openMenu === dd && to && !root.contains(to)) closeDropdown(dd);
    });

    // A followed link closes it, also when the page does not change (a
    // hash). A modified click opens a tab and leaves the panel up.
    root.addEventListener("click", function (e) {
      if (openMenu !== dd || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      if (e.target && e.target.closest && e.target.closest("a")) closeDropdown(dd);
    });

    return dd;
  }

  /* ---------- Sheet ---------- */

  function setSheet(open) {
    var sheet = document.querySelector("[data-nav-sheet]");
    var burger = nav ? nav.querySelector("[data-nav-burger]") : null;
    if (!sheet || !burger || open === sheetOpen) return;
    sheetOpen = open;
    sheet.hidden = !open;
    burger.setAttribute("aria-expanded", open ? "true" : "false");
    burger.setAttribute("aria-label", open ? "Close menu" : "Open menu");
    var bar1 = burger.querySelector('[data-nav-bar="1"]');
    var bar2 = burger.querySelector('[data-nav-bar="2"]');
    var bar3 = burger.querySelector('[data-nav-bar="3"]');
    if (bar1) bar1.style.transform = open ? "rotate(45deg) translate(5px, 5px)" : "none";
    if (bar2) bar2.style.opacity = open ? "0" : "1";
    if (bar3) bar3.style.transform = open ? "rotate(-45deg) translate(5px, -5px)" : "none";

    if (open) {
      var onKey = function (e) {
        // A dialog over the sheet (the cart drawer) answers its own keys.
        if (e.defaultPrevented || document.querySelector('[aria-modal="true"]')) return;
        if (e.key === "Escape") {
          setSheet(false);
          burger.focus();
          return;
        }
        if (e.key !== "Tab") return;
        // The trap: the menu button (it reads "Close menu" now), then the
        // sheet's links in order, and round again.
        var items = [burger];
        var found = sheet.querySelectorAll(SHEET_FOCUSABLE);
        for (var k = 0; k < found.length; k++) {
          if (found[k].getClientRects().length) items.push(found[k]);
        }
        var at = items.indexOf(document.activeElement);
        var to = e.shiftKey ? (at <= 0 ? items.length - 1 : at - 1) : (at < 0 || at === items.length - 1 ? 0 : at + 1);
        e.preventDefault();
        items[to].focus();
      };
      var wide = window.matchMedia("(min-width: " + FULL_ROW_PX + "px)");
      var onWide = function () {
        if (wide.matches) setSheet(false);
      };
      document.addEventListener("keydown", onKey);
      if (wide.addEventListener) wide.addEventListener("change", onWide);
      else if (wide.addListener) wide.addListener(onWide);
      var root = document.documentElement;
      var prevOverflow = root.style.overflow;
      root.style.overflow = "hidden";
      sheetCleanup = function () {
        document.removeEventListener("keydown", onKey);
        if (wide.removeEventListener) wide.removeEventListener("change", onWide);
        else if (wide.removeListener) wide.removeListener(onWide);
        root.style.overflow = prevOverflow;
      };
    } else if (sheetCleanup) {
      sheetCleanup();
      sheetCleanup = null;
    }
    setSolid();
  }

  /* ---------- Cart ---------- */

  function setCartCount(count) {
    var n = Number(count) || 0;
    var label = n ? "Open cart, " + n + " item" + (n === 1 ? "" : "s") : "Open cart";
    var buttons = document.querySelectorAll("[data-cart-open]");
    var rose = false;
    for (var i = 0; i < buttons.length; i++) {
      buttons[i].setAttribute("aria-label", label);
      var badge = buttons[i].querySelector("[data-cart-count]");
      if (!badge) continue;
      if (n > (Number(badge.textContent) || 0)) rose = true;
      badge.textContent = String(n);
      badge.style.display = n > 0 ? "flex" : "none";
    }
    if (rose) bumpLater();
  }

  /* Motion preview (mo-cart, pick 1; docs/motion-preview.md): when the
     count goes up, the badge flashes white for 450ms and returns to gold.
     The count changes before the cart drawer opens, so the flash waits
     for the drawer to close (bakes:cart-close); with no drawer open by
     then (the cart page, a failed open) it plays on its own. Asked at the
     moment it plays, so the MOTION panel can switch it. The animation is
     in bakes.css (MOTION PREVIEW), inside reduced-motion: no-preference. */
  var BUMP_WAIT_MS = 700;
  var bumpPending = false;
  var bumpTimer = 0;

  function bumpLater() {
    bumpPending = true;
    clearTimeout(bumpTimer);
    bumpTimer = setTimeout(function () {
      if (!document.getElementById("bakes-cart-title")) bump();
    }, BUMP_WAIT_MS);
  }

  function bump() {
    if (!bumpPending) return;
    bumpPending = false;
    clearTimeout(bumpTimer);
    if (!document.documentElement.classList.contains("mo-cart")) return;
    // No animation would run to take the mark off again.
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    var badges = document.querySelectorAll("[data-cart-open] [data-cart-count]");
    for (var i = 0; i < badges.length; i++) {
      var b = badges[i];
      b.classList.remove("is-bump");
      void b.offsetWidth;
      b.classList.add("is-bump");
    }
  }

  function openCart() {
    // No drawer on the page (it failed to load, or is not built yet): the
    // cart page instead, so the button never does nothing.
    if (!document.querySelector('[data-island="cart-drawer"]')) {
      var url = (nav && nav.getAttribute("data-cart-url")) || "/cart";
      window.location.assign(url);
      return;
    }
    window.dispatchEvent(new CustomEvent("bakes:cart-open"));
  }

  function refreshCart() {
    var url = ((nav && nav.getAttribute("data-cart-url")) || "/cart") + ".js";
    if (!window.fetch) return;
    window.fetch(url, { credentials: "same-origin", headers: { Accept: "application/json" } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (cart && typeof cart.item_count === "number") setCartCount(cart.item_count);
      })
      .catch(function () { /* the server count stands */ });
  }

  /* ---------- ?look= lead ---------- */

  function applyLook() {
    if (document.documentElement.getAttribute("data-look") !== "bakes") return;
    var thumbs = document.querySelectorAll("[data-look-src]");
    for (var i = 0; i < thumbs.length; i++) {
      var img = thumbs[i].querySelector("img");
      var src = thumbs[i].getAttribute("data-look-src");
      if (!img || !src) continue;
      img.removeAttribute("srcset");
      img.src = src;
    }
  }

  /* ---------- Motion preview: logo bar, section rules, press states ---------- */

  /* "Logo and section lines draw" (mo-lines, pick 6; docs/motion-preview.md).
     The CSS is in bakes.css (MOTION PREVIEW, brand group), inside
     prefers-reduced-motion: no-preference; the default is always the
     finished state, so without this script nothing waits. */
  var LOGO_KEY = "bakes:logo_seen";
  // Past this, the CSS fallback in bakes.css has started the draw on its
  // own (4s after first paint); drawing again would redraw a drawn bar.
  var LOGO_LATE_MS = 3800;

  function motionReduced() {
    return !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  }

  // The home hero's bar (the layout's head script marked <html>
  // data-logo-wait: not yet seen this session) draws once the window has
  // loaded. Asked then, so with mo-lines off nothing draws and the visit's
  // one draw is not spent.
  function logoDraw() {
    var root = document.documentElement;
    if (!root.hasAttribute("data-logo-wait")) return;
    var go = function () {
      if (!root.hasAttribute("data-logo-wait")) return;
      var on = root.classList.contains("mo-lines") && !motionReduced() &&
        !!document.querySelector("[data-hero-logo] .bk-logo-bar");
      var early = !window.performance || window.performance.now() < LOGO_LATE_MS;
      root.removeAttribute("data-logo-wait");
      if (!on) return;
      try { window.sessionStorage.setItem(LOGO_KEY, "1"); } catch (e) { /* draws again next page */ }
      if (!early) return;
      root.setAttribute("data-logo-draw", "");
      window.setTimeout(function () { root.removeAttribute("data-logo-draw"); }, 700);
    };
    if (document.readyState === "complete") go();
    else window.addEventListener("load", go, { once: true });
  }

  // Each section header's 40px gold rule draws as it comes into view, once.
  // Headers on screen (or above it) when the page opens are left drawn;
  // the rest are marked bk-rule-wait (undrawn, while mo-lines is on) until
  // they enter. Nothing is marked without IntersectionObserver or under
  // reduced motion, so the rules simply stay drawn.
  function ruleReveal() {
    if (!("IntersectionObserver" in window) || motionReduced()) return;
    var heads = document.querySelectorAll(".section-header");
    if (!heads.length) return;
    var vh = window.innerHeight || document.documentElement.clientHeight;
    var io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (!entries[i].isIntersecting) continue;
        entries[i].target.classList.remove("bk-rule-wait");
        io.unobserve(entries[i].target);
      }
    }, { rootMargin: "0px 0px -10% 0px" });
    for (var i = 0; i < heads.length; i++) {
      if (heads[i].getBoundingClientRect().top < vh * 0.9) continue;
      heads[i].classList.add("bk-rule", "bk-rule-wait");
      io.observe(heads[i]);
    }
  }

  var motionBound = false;
  function motionInit() {
    if (motionBound) return;
    motionBound = true;
    logoDraw();
    ruleReveal();
    // "Hover and tap feel" (mo-hover, pick 5): iOS Safari applies :active
    // only on a page that listens for touches. The press states themselves
    // are CSS, scoped under html.mo-hover; this listener does nothing.
    document.addEventListener("touchstart", function () {}, { passive: true });
  }

  /* ---------- Wiring ---------- */

  function init() {
    nav = document.querySelector("[data-bakes-nav]");
    if (!nav || nav.hasAttribute("data-nav-ready")) return;
    nav.setAttribute("data-nav-ready", "");

    openMenu = null;
    sheetOpen = false;
    dropdowns = [];
    var roots = nav.querySelectorAll("[data-nav-dd]");
    for (var i = 0; i < roots.length; i++) {
      var dd = setupDropdown(roots[i]);
      if (dd) dropdowns.push(dd);
    }

    var burger = nav.querySelector("[data-nav-burger]");
    if (burger) {
      burger.addEventListener("click", function () { setSheet(!sheetOpen); });
    }
    var sheet = document.querySelector("[data-nav-sheet]");
    if (sheet) {
      var scrim = sheet.querySelector("[data-nav-scrim]");
      if (scrim) scrim.addEventListener("click", function () { setSheet(false); });
      // A tap on the current page's own link changes no page, so every link
      // closes the sheet by hand.
      sheet.addEventListener("click", function (e) {
        if (e.target && e.target.closest && e.target.closest("a")) setSheet(false);
      });
    }

    var carts = document.querySelectorAll("[data-cart-open]");
    for (var j = 0; j < carts.length; j++) carts[j].addEventListener("click", openCart);

    applyLook();
    scrolled = false;
    onScroll();
    setSolid();

    if (!docListenersBound) {
      docListenersBound = true;
      window.addEventListener("scroll", onScroll, { passive: true });
      window.addEventListener("bakes:cart-updated", function (e) {
        var cart = e && e.detail;
        if (cart && typeof cart.item_count === "number") setCartCount(cart.item_count);
      });
      // The drawer closed: a count that went up while it was open flashes now (mo-cart).
      window.addEventListener("bakes:cart-close", bump);
      document.addEventListener("animationend", function (e) {
        if (e.animationName === "bk-badge" && e.target && e.target.classList) e.target.classList.remove("is-bump");
      });
      // Back and Forward can restore this page from the cache with a menu
      // still open and an old count: close everything and ask the cart again.
      window.addEventListener("pageshow", function (e) {
        if (!e.persisted) return;
        if (openMenu) closeDropdown(openMenu);
        setSheet(false);
        refreshCart();
      });
    }
  }

  // Theme editor: a re-rendered header is a new nav.
  document.addEventListener("shopify:section:unload", function (e) {
    if (!e.target || !e.target.querySelector || !e.target.querySelector("[data-bakes-nav]")) return;
    if (openMenu) closeDropdown(openMenu);
    setSheet(false);
    nav = null;
  });
  document.addEventListener("shopify:section:load", function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector("[data-bakes-nav]")) init();
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
    document.addEventListener("DOMContentLoaded", motionInit, { once: true });
  } else {
    init();
    motionInit();
  }
})();
